import React, { useState, useEffect } from "react";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import ViewMyInsights from "./VizOrganizationSearch";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import ReportCard from "../../components/ReportCard";
import { useLazyGetPortalUserInfoQuery } from "../../slices/dataviewApiSlice";
import { setPortalUserData } from "../../slices/viewReportSlice";
import {
    getSelectedIsfOrganizationIds,
    getEnabledDashboardTiles,
} from "../../utils/helpers";
import { SNAPSHOTDATAVIEWTYPE } from "../../constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useLazyGetReportDetailsQuery } from "../../slices/saveReportViewSlice";
import Loader from "../../components/ui/Loader";
import RequestFailed from "../../components/alerts/RequestFailed";
import { setSnapshotReportDetails } from "../../slices/disabilitySnapshotSlice";

function Dashboard(props) {
    const dispatch = useDispatch();
    const flags = useFlags();
    const { user } = useSelector((state) => state.auth);
    const [loadingViewData, setLoadingViewData] = useState(false);
    const [organizationsSelected, setOrganizationsSelected] = useState([]);
    const { totalParentOrganizations } = useSelector(
        (state) => state.organizations
    );
    const {
        parentOrganizations: organizations,
        organizations: allOrganizations,
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
    } = useSelector((state) => state.userDetails);
    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
        portalUserData: storedPortalUserData,
    } = useSelector((state) => state.reportOrganization);

    // Memoize derived values for performance
    const selectedIsfOrgMemo = React.useMemo(() => {
        return getSelectedIsfOrganizationIds(
            userSelectedOrganizationReports,
            reportId,
            selectedRelatedOrganizations,
            totalParentOrganizations
        );
    }, [
        userSelectedOrganizationReports,
        reportId,
        selectedRelatedOrganizations,
        totalParentOrganizations,
    ]);
    const selectedIsfOrganizationIds =
        selectedIsfOrgMemo.selectedIsfOrganizationIds;
    const organizationGroupId = selectedIsfOrgMemo.organizationGroupId;

    const displayReportView = React.useMemo(() => {
        return totalParentOrganizations === 1 &&
            userSelectedOrganizationReports[reportId]?.related?.length === 0
            ? true
            : userSelectedOrganizationReports[reportId]?.related?.length > 0
            ? selectedRelatedOrganizations?.length > 0
            : organizationsSelected?.length > 0;
    }, [
        totalParentOrganizations,
        userSelectedOrganizationReports,
        reportId,
        selectedRelatedOrganizations,
        organizationsSelected,
    ]);

    //portal user data
    const [getPortalUser, { data: portalUserData }] =
        useLazyGetPortalUserInfoQuery();
    useEffect(() => {
        if (userSelectedOrganizationReports && portalUserData) {
            dispatch(setPortalUserData(portalUserData?.userPermissions));
        }
    }, [portalUserData, dispatch, userSelectedOrganizationReports]);

    //fetch data-view api
    const [
        getReportDetails,
        { data: reportDetailsData, isFetching, isError: isReportDetalisError },
    ] = useLazyGetReportDetailsQuery();
    useEffect(() => {
        if (reportDetailsData) {
            dispatch(setSnapshotReportDetails(reportDetailsData));
        }
    }, [reportDetailsData, dispatch]);

    const reportDetailsParams = React.useMemo(() => {
        const params = {
            isfUserId: user.userId,
            dataViewType: SNAPSHOTDATAVIEWTYPE,
            selectedIsfOrganizationIds: selectedIsfOrganizationIds,
        };

        if (organizationGroupId) {
            params.organizationGroupId = organizationGroupId;
        }
        return params;
    }, [user.userId, selectedIsfOrganizationIds, organizationGroupId]);

    const enabledReportItems = getEnabledDashboardTiles({
        user,
        flags,
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
        allOrganizations,
    });

    const relatedOrganizationIDISF = React.useMemo(
        () =>
            userSelectedOrganizationReports[reportId] &&
            userSelectedOrganizationReports[reportId]?.related?.map(
                (item) => item.ORGANIZATIONIDISF
            ),
        [userSelectedOrganizationReports, reportId]
    );

    const organizationIDISF = React.useMemo(
        () =>
            userSelectedOrganizationReports[reportId] && [
                userSelectedOrganizationReports[reportId]?.organization
                    ?.ORGANIZATIONIDISF,
            ],
        [userSelectedOrganizationReports, reportId]
    );

    const assignedOrgs = React.useMemo(
        () =>
            storedPortalUserData?.assignedOrgs?.map((obj) =>
                obj.orgId.toLowerCase()
            ),
        [storedPortalUserData]
    );

    const orgsToCompare = React.useMemo(
        () =>
            userSelectedOrganizationReports[reportId]?.related?.length > 0
                ? relatedOrganizationIDISF
                : organizationIDISF,
        [
            userSelectedOrganizationReports,
            reportId,
            relatedOrganizationIDISF,
            organizationIDISF,
        ]
    );

    const sortedAssignedOrgs = React.useMemo(
        () => assignedOrgs?.slice().sort(),
        [assignedOrgs]
    );
    const sortedOrgsToCompare = React.useMemo(
        () => orgsToCompare?.map((org) => org?.toLowerCase())?.sort(),
        [orgsToCompare]
    );

    // Check if the arrays are of the same length and their elements match
    const areArraysEqual = React.useMemo(
        () =>
            sortedAssignedOrgs?.length === sortedOrgsToCompare?.length &&
            sortedAssignedOrgs?.every(
                (value, index) => value === sortedOrgsToCompare[index]
            ),
        [sortedAssignedOrgs, sortedOrgsToCompare]
    );

    useEffect(() => {
        if (userSelectedOrganizationReports[reportId] && !areArraysEqual) {
            getPortalUser({
                isfUserId: String(user.userId).toUpperCase(),
                organizationId:
                    userSelectedOrganizationReports[reportId]?.related?.length >
                    0
                        ? relatedOrganizationIDISF
                        : organizationIDISF,
            });
        }
    }, [
        userSelectedOrganizationReports,
        reportId,
        areArraysEqual,
        getPortalUser,
        user.userId,
        relatedOrganizationIDISF,
        organizationIDISF,
    ]);

    useEffect(() => {
        if (displayReportView || organizations.length === 1) {
            getReportDetails(reportDetailsParams);
        }
    }, [
        displayReportView,
        organizations.length,
        reportDetailsParams,
        getReportDetails,
    ]);

    return (
        <>
            <Breadcrumb links={[{ path: "/dashboard", name: "Dashboards" }]} />
            <ViewMyInsights
                previousUrl={props?.previousUrl}
                onViewDataLoading={setLoadingViewData}
                loadingViewData={loadingViewData}
                setOrganizationsSelected={setOrganizationsSelected}
            />
            <div>
                {!isFetching && !isReportDetalisError && (
                    <div className="report-card-views">
                        {enabledReportItems.map((item) => (
                            <div key={item.path}>
                                <ReportCard
                                    key={item.path}
                                    {...item}
                                    displayCards={displayReportView}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isFetching && <Loader />}
            {isReportDetalisError && !isFetching && (
                <div className="mt-20p">
                    <RequestFailed />
                </div>
            )}
        </>
    );
}

Dashboard.propTypes = {
    previousUrl: PropTypes.array.isRequired,
};

export default Dashboard;
